import { cocartClient } from './client';

export interface ProductImage {
    id: number;
    src: string;
    alt: string;
}

export interface ProductAttribute {
    id: number;
    name: string;
    slug: string;
    options: string[];
    variation: boolean;
    visible: boolean;
}

export interface ProductVariation {
    id: number;
    attributes: Array<{
        id: number;
        name: string;
        slug: string;
        option: string;
    }>;
    price: string;
    regular_price: string;
    sale_price: string;
    stock_status: 'instock' | 'outofstock' | 'onbackorder';
    stock_quantity: number | null;
    image?: ProductImage;
    sku: string;
}

export interface Product {
    id: number;
    name: string;
    slug: string;
    permalink: string;
    description: string;
    short_description: string;
    price: string;
    regular_price: string;
    sale_price: string;
    stock_status: 'instock' | 'outofstock' | 'onbackorder';
    stock_quantity: number | null;
    images: ProductImage[];
    attributes: ProductAttribute[];
    variations: number[];
    type: 'simple' | 'variable' | 'grouped' | 'external';
    categories: Array<{ id: number; name: string; slug: string }>;
    average_rating: string;
    rating_count: number;
}

export interface ProductsQueryParams {
    page?: number;
    per_page?: number;
    search?: string;
    category?: string | number;
    orderby?: 'date' | 'id' | 'title' | 'price' | 'popularity' | 'rating';
    order?: 'asc' | 'desc';
    min_price?: string | number;
    max_price?: string | number;
    stock_status?: 'instock' | 'outofstock' | 'onbackorder';
}

export interface ProductsResponse {
    data: Product[];
    total: number;
    totalPages: number;
    currentPage: number;
}

// Mapowanie wartości orderby dla CoCart (zgodnie z działającym kodem
// referencyjnym — mapowanie jest tożsamościowe, ale zostawiamy je 1:1).
const mapOrderBy = (orderby?: string): string => {
    const mapping: Record<string, string> = {
        date: 'date',
        id: 'id',
        title: 'title',
        price: 'price',
        popularity: 'popularity',
        rating: 'rating',
    };
    return mapping[orderby || 'date'] || 'date';
};

// CoCart w niektórych konfiguracjach zwraca ceny w najmniejszej jednostce
// waluty (grosze) — jeśli cena jest liczbą całkowitą większą od 100,
// traktujemy ją jako grosze i przeliczamy na złote.
const convertPrice = (price: string | undefined): string => {
    if (!price) return '0';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '0';
    if (Number.isInteger(numPrice) && numPrice > 100) {
        return (numPrice / 100).toString();
    }
    return price;
};

// Odczytuje URL obrazu z różnych kształtów odpowiedzi CoCart.
const getImageUrl = (img: any): string => {
    if (!img) return '';
    if (typeof img === 'string') return img;

    if (typeof img === 'object') {
        if (img.src) {
            if (typeof img.src === 'object') {
                return img.src.full || img.src.large || img.src.medium || img.src.thumbnail || img.src.custom || '';
            }
            if (typeof img.src === 'string') return img.src;
        }
        if (img.url) return img.url;
        if (img.source) return img.source;
        if (img.guid) return img.guid;
        if (img.link) return img.link;
        if (img.full) return img.full;
        if (img.large) return img.medium;
        if (img.medium) return img.medium;
        if (img.thumbnail) return img.thumbnail;
        if (img.custom) return img.custom;
    }

    return '';
};

const normalizeProduct = (product: any): Product => {
    let normalizedAttributes = product.attributes;
    if (product.attributes && typeof product.attributes === 'object' && !Array.isArray(product.attributes)) {
        normalizedAttributes = Object.values(product.attributes);
    }
    if (!normalizedAttributes) normalizedAttributes = [];

    let normalizedImages: ProductImage[] = [];
    if (product.images) {
        let imageArray: any[] = [];
        if (Array.isArray(product.images)) {
            imageArray = product.images;
        } else if (typeof product.images === 'object') {
            imageArray = Object.values(product.images);
        }

        normalizedImages = imageArray
            .filter((img: any) => img)
            .map((img: any) => ({
                id: img?.id || 0,
                src: getImageUrl(img),
                alt: img?.alt || product.name || '',
            }))
            .filter((img: ProductImage) => img.src && img.src.length > 0);
    }

    let normalizedCategories = product.categories;
    if (product.categories && typeof product.categories === 'object' && !Array.isArray(product.categories)) {
        normalizedCategories = Object.values(product.categories);
    }
    if (!normalizedCategories) normalizedCategories = [];

    let price = product.price || '0';
    let regularPrice = product.regular_price || '0';
    let salePrice = product.sale_price || '';

    // Niektóre wersje CoCart zwracają ceny w zagnieżdżonym obiekcie "prices".
    if (product.prices) {
        price = product.prices.price || price;
        regularPrice = product.prices.regular_price || regularPrice;
        salePrice = product.prices.sale_price || '';
    }

    return {
        ...product,
        price: convertPrice(price),
        regular_price: convertPrice(regularPrice),
        sale_price: salePrice ? convertPrice(salePrice) : '',
        attributes: normalizedAttributes,
        images: normalizedImages,
        categories: normalizedCategories,
        variations: product.variations || [],
    };
};

export const getProducts = async (params?: ProductsQueryParams): Promise<ProductsResponse> => {
    try {
        const apiParams: Record<string, any> = {
            per_page: params?.per_page || 12,
            page: params?.page || 1,
        };

        if (params?.search) apiParams.search = params.search;
        if (params?.category) apiParams.category = params.category;
        if (params?.min_price) apiParams.min_price = params.min_price;
        if (params?.max_price) apiParams.max_price = params.max_price;
        if (params?.stock_status) apiParams.stock_status = params.stock_status;

        if (params?.orderby) {
            apiParams.orderby = mapOrderBy(params.orderby);
            apiParams.order = params.order || 'desc';
        }

        const response = await cocartClient.get('/products', { params: apiParams });

        let productsData: any[] = [];
        if (response.data?.products && Array.isArray(response.data.products)) {
            productsData = response.data.products;
        } else if (Array.isArray(response.data)) {
            productsData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
            productsData = response.data.data;
        }

        const normalizedProducts = productsData.map(normalizeProduct);

        const total = parseInt(response.headers['x-wp-total'] || response.data?.total || '0');
        const totalPages = parseInt(response.headers['x-wp-totalpages'] || response.data?.total_pages || '0');

        return {
            data: normalizedProducts,
            total: total || normalizedProducts.length,
            totalPages: totalPages || 1,
            currentPage: params?.page || 1,
        };
    } catch (error) {
        console.error('❌ Błąd pobierania produktów z CoCart:', error);
        throw error;
    }
};

export const getProductById = async (id: number): Promise<Product> => {
    try {
        const response = await cocartClient.get(`/products/${id}`);
        return normalizeProduct(response.data);
    } catch (error) {
        console.error(`❌ Błąd pobierania produktu ${id} z CoCart:`, error);
        throw error;
    }
};

export const getProductVariations = async (productId: number): Promise<ProductVariation[]> => {
    try {
        const response = await cocartClient.get(`/products/${productId}/variations`);

        let variationsData: any[] = [];
        if (Array.isArray(response.data)) {
            variationsData = response.data;
        } else if (response.data?.variations && Array.isArray(response.data.variations)) {
            variationsData = response.data.variations;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
            variationsData = response.data.data;
        }

        return variationsData.map((variation: any) => ({
            ...variation,
            price: convertPrice(variation.price),
            regular_price: convertPrice(variation.regular_price),
            sale_price: variation.sale_price ? convertPrice(variation.sale_price) : '',
        }));
    } catch (error) {
        console.error(`❌ Błąd pobierania wariantów produktu ${productId} z CoCart:`, error);
        throw error;
    }
};
